import {type FC, useCallback, useEffect} from 'react';

import NiceModal from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';

import type {OnMoreIconClick, Props} from './types';

import {ContextMenu} from '../../../../../../components/ContextMenu';
import {useInjection, withModuleLoaderHOC} from '../../../../../../IoC';
import {logger} from '../../../../../../utils/logger';
import {trackersModule} from '../../../IoC';
import {AddWeightModal} from '../../modals/AddWeightModal';
import {CommentBlock} from './components/CommentBlock';
import {CurrentWeightBlock} from './components/CurrentWeightBlock';
import {ReportingBlock} from './components/ReportingBlock';
import {
  COMMENT_CONTEXT_MENU_ID,
  useTrackersComment,
} from './hooks/useTrackersComment';
import * as S from './styles';

export const TrackersContainer: FC<Props> = withModuleLoaderHOC(
  {
    modules: [trackersModule],
  },
  ({onMoreIconClick, renderCurrentDate}) => {
    const store = useInjection(
      trackersModule.exports.TrackersStore,
      trackersModule.id,
    );

    useEffect(() => {
      store.init();
    }, [store]);

    const {t} = useI18n();
    const comment = useTrackersComment({store, t});

    const onAddWeightClick = useCallback(() => {
      logger.event('add_weight_tap');

      NiceModal.show(AddWeightModal, {
        defaultUnit: store.selectedUnit,
        defaultWeight: store.currentWeight?.weight ?? undefined,
        onSubmit: result => store.trackWeight(result),
        onSubmitComment: async (text: string) => {
          const trimmed = text.trim();

          if (!trimmed) return;

          await store.createDiaryEntry({
            content: trimmed,
            type: 'weight_tracker',
          });
        },
      });
    }, [store]);

    const onMoreCurrentWeightClick = useCallback(
      (e: Parameters<OnMoreIconClick>[0]) => {
        e.event.DOMEvent?.stopPropagation?.();

        const actions = store.getTrackersActions('current_weight');

        onMoreIconClick?.(e, 'current_weight', actions);
      },
      [onMoreIconClick, store],
    );

    const onMoreReportingClick = useCallback(
      (e: Parameters<OnMoreIconClick>[0]) => {
        e.event.DOMEvent?.stopPropagation?.();

        const actions = store.getTrackersActions('reporting');

        onMoreIconClick?.(e, 'reporting', actions);
      },
      [onMoreIconClick, store],
    );

    return (
      <>
        <S.Container>
          <Observer>
            {() =>
              store.isWeightHistoryBlockVisible ? (
                <>
                  <S.RowBlock>
                    <CurrentWeightBlock
                      onAddWeightClick={onAddWeightClick}
                      onMoreCurrentWeightClick={onMoreCurrentWeightClick}
                      renderCurrentDate={renderCurrentDate}
                      store={store}
                      t={t}
                    />
                    <ReportingBlock
                      onMoreReportingClick={onMoreReportingClick}
                      store={store}
                      t={t}
                    />
                  </S.RowBlock>
                  <CommentBlock
                    commentText={comment.commentText}
                    handleSubmitComment={comment.handleSubmitComment}
                    isCommentInputVisible={comment.isCommentInputVisible}
                    onAddCommentClick={comment.onAddCommentClick}
                    onCancelCommentClick={comment.onCancelCommentClick}
                    onMoreCommentClick={comment.onMoreCommentClick}
                    setCommentText={comment.setCommentText}
                    store={store}
                    t={t}
                  />
                </>
              ) : (
                <S.RowBlock>
                  <CurrentWeightBlock
                    onAddWeightClick={onAddWeightClick}
                    onMoreCurrentWeightClick={onMoreCurrentWeightClick}
                    renderCurrentDate={renderCurrentDate}
                    store={store}
                    t={t}
                  />
                  <S.RightColumn>
                    <ReportingBlock
                      onMoreReportingClick={onMoreReportingClick}
                      store={store}
                      t={t}
                    />
                    <CommentBlock
                      commentText={comment.commentText}
                      handleSubmitComment={comment.handleSubmitComment}
                      isCommentInputVisible={comment.isCommentInputVisible}
                      onAddCommentClick={comment.onAddCommentClick}
                      onCancelCommentClick={comment.onCancelCommentClick}
                      onMoreCommentClick={comment.onMoreCommentClick}
                      setCommentText={comment.setCommentText}
                      store={store}
                      t={t}
                    />
                  </S.RightColumn>
                </S.RowBlock>
              )
            }
          </Observer>
        </S.Container>
        <ContextMenu.Menu animation="fade" id={COMMENT_CONTEXT_MENU_ID}>
          <ContextMenu.Item
            onClick={comment.handleOnDeleteCommentItemClick}
            variant="error"
          >
            {t('shared:delete')}
          </ContextMenu.Item>
        </ContextMenu.Menu>
      </>
    );
  },
);
